import type { ChargerAuthMethod, ChargerCommandBody, ChargerUpdateBody, ConnectorStatus, PricedPeriod, RateScheduleBody, TariffBody, TariffDefaults, TariffKind, TouPeriod } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put } from './client';

export interface ConnectorState {
  status: ConnectorStatus;
  errorCode?: string;
  vendorErrorCode?: string;
  info?: string;
  at: string;
}

export interface ActiveSession {
  id: string;
  ocppTransactionId: number;
  connectorId: number;
  startedAt: string;
  energyWh: number;
  powerW: number | null;
  idTag: string | null;
  billingType: string;
  driverName: string | null;
}

export interface Charger {
  id: string;
  chargePointId: string;
  label: string | null;
  bay: string | null;
  /** Its own spot on the map (chargers spread around one site); null: the site's location. */
  latitude: string | null;
  longitude: string | null;
  siteId: string | null;
  siteName: string | null;
  organisationId: string | null;
  organisationName: string | null;
  /** Active sites of its organisation (with one, screens show the organisation, not the site). */
  organisationSiteCount: number;
  vendor: string | null;
  model: string | null;
  serialNumber: string | null;
  firmwareVersion: string | null;
  vendorProfile: string | null;
  registration: 'pending' | 'accepted' | 'rejected';
  securityProfile: number;
  hasPassword: boolean;
  authMethod: ChargerAuthMethod;
  assignedDriverId: string | null;
  assignedDriverName: string | null;
  publicCharging: boolean;
  /** Each socket's code for its QR sticker (/charge/K7M-2QX), stored without the dash; while public charging is on. */
  chargeCodes: { id: string; connectorId: number; code: string; createdAt: string }[];
  publicDemo: boolean;
  ratedKw: string | null;
  /** Plug on each connector, in connector order. */
  plugTypes: string[];
  connectorCount: number;
  connectorStatus: Record<string, ConnectorState>;
  /** The public price (pay to charge) in force now, and the next one if a change is scheduled. */
  tariff: TariffSummary | null;
  nextTariff: TariffSummary | null;
  /** The private price (RFID cards) in force now, and the next one. */
  privateTariff: TariffSummary | null;
  nextPrivateTariff: TariffSummary | null;
  /** The site's peak and off-peak hours; null for one price all day. */
  rateSchedule: { id: string; name: string; periods: TouPeriod[] } | null;
  installedOn: string | null;
  isConnected: boolean;
  lastSeenAt: string | null;
  lastBootAt: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  activeSessions: ActiveSession[];
  lastConfiguration?: Record<string, string | null> | null;
}

export interface TariffSummary {
  id: string;
  kind: TariffKind;
  /** The price, or with peak and off-peak, the first period's. */
  pricePerKwh: number;
  /** Peak, off-peak… with their prices and the hours they were set with; null for one price all day. */
  periods: PricedPeriod[] | null;
  gstInclusive: boolean;
  idleFeePerMinute: number | null;
  idleGraceMinutes: number;
  /** The most idle fee a session, $ (null: an hour's worth for guests, no limit for RFID cards). */
  idleFeeMax: number | null;
  /** Infinity EV's share (null on prices set before shares were kept). */
  infinityevRatePerKwh: number | null;
  infinityevIdleFeePerMinute: number | null;
  cardFeePercent: number | null;
  validFrom: string;
}

export interface Tariff extends Omit<TariffSummary, 'validFrom'> {
  validFrom: string;
  validUntil: string | null;
  createdAt: string;
  createdBy: string | null;
  state: 'scheduled' | 'current' | 'past';
}

export interface CommandResult {
  id: string;
  action: string;
  status: 'pending' | 'sent' | 'accepted' | 'rejected' | 'error' | 'timeout';
  response: { status?: string; configurationKey?: { key: string; value?: string; readonly?: boolean }[]; unknownKey?: string[] } | null;
  errorCode: string | null;
  errorDescription: string | null;
}

export interface CommandHistoryRow extends CommandResult {
  payload: Record<string, unknown>;
  createdAt: string;
  completedAt: string | null;
  requestedBy: string | null;
}

export interface OcppMessage {
  id: string;
  direction: 'in' | 'out';
  messageTypeId: 2 | 3 | 4;
  messageId: string;
  action: string | null;
  payload: unknown;
  errorCode: string | null;
  errorDescription: string | null;
  createdAt: string;
}

export const chargersKey = ['chargers'] as const;
/** Settings, QR stickers: which socket each is on changes with chargers. */
export const qrStickersKey = ['qr-stickers'] as const;

export const useChargers = () => useQuery({ queryKey: [...chargersKey, 'list'], queryFn: () => get<Charger[]>('/chargers') });
/** Chargers that were removed (retired), for restoring. Admins only. */
export const useRemovedChargers = (enabled: boolean) =>
  useQuery({ queryKey: [...chargersKey, 'list', 'removed'], queryFn: async () => (await get<Charger[]>('/chargers?include=all')).filter((c) => !c.isActive), enabled });
export const useCharger = (id: string) => useQuery({ queryKey: [...chargersKey, id], queryFn: () => get<Charger>(`/chargers/${id}`) });
export const useCommandHistory = (id: string) => useQuery({ queryKey: [...chargersKey, id, 'commands'], queryFn: () => get<CommandHistoryRow[]>(`/chargers/${id}/commands`) });
export const useOcppMessages = (id: string, hideHeartbeats: boolean) =>
  useQuery({ queryKey: [...chargersKey, id, 'messages', hideHeartbeats], queryFn: () => get<OcppMessage[]>(`/chargers/${id}/messages?hideHeartbeats=${hideHeartbeats}&limit=200`) });

function useChargerMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chargersKey });
      void qc.invalidateQueries({ queryKey: ['organisations'] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: qrStickersKey });
    },
  });
}

export const useUpdateCharger = () => useChargerMutation(({ id, ...body }: ChargerUpdateBody & { id: string }) => patch<Charger>(`/chargers/${id}`, body));
export const useAcceptCharger = () =>
  useChargerMutation(({ id, ...body }: { id: string; siteId: string; label?: string; bay?: string }) => post<Charger>(`/chargers/${id}/accept`, body));
/** Add a charger by hand; if one with that ID is already waiting, that one is accepted. */
export const useCreateCharger = () =>
  useChargerMutation((body: { chargePointId: string; siteId: string; label?: string; bay?: string; connectorCount: number }) => post<Charger & { wasWaiting: boolean }>('/chargers', body));
/** Erased if never used, otherwise retired (history kept). */
export const useRemoveCharger = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<{ erased: boolean }>(`/chargers/${id}`),
    // Refresh the lists only: an open dialog for this charger mustn't refetch one that's gone.
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...chargersKey, 'list'] }),
  });
};
export const useRejectCharger = () => useChargerMutation((id: string) => post<Charger>(`/chargers/${id}/reject`));
export const useChargerSecurity = () =>
  useChargerMutation(({ id, ...body }: { id: string; securityProfile: 0 | 1 | 2; password?: string; sendToCharger: boolean }) => post<Charger>(`/chargers/${id}/security`, body));
/** A new code for one socket's sticker; the old one stops working. */
export const useReplaceChargeCode = () =>
  useChargerMutation(({ id, connectorId, code }: { id: string; connectorId: number; code: string | null }) => post<Charger>(`/chargers/${id}/charge-codes/${connectorId}/replace`, { code }));
export const useSendCommand = () =>
  useChargerMutation(({ id, ...body }: ChargerCommandBody & { id: string }) => post<CommandResult>(`/chargers/${id}/commands`, body));

export const useTariffs = (chargerId: string, kind: TariffKind) =>
  useQuery({ queryKey: [...chargersKey, chargerId, 'tariffs', kind], queryFn: () => get<Tariff[]>(`/tariffs?chargerId=${chargerId}&kind=${kind}`) });
/** Where a new price starts from: Infinity EV's share and the card fee (admins). */
export const useTariffDefaults = (enabled = true) => useQuery({ queryKey: [...chargersKey, 'tariff-defaults'], queryFn: () => get<TariffDefaults>('/tariffs/defaults'), enabled, staleTime: 5 * 60_000 });
export const useSetTariff = () => useChargerMutation((body: TariffBody) => post<{ count: number; validFrom: string }>('/tariffs', body));
export const useCancelTariff = () => useChargerMutation((id: string) => del(`/tariffs/${id}`));

// ---- Peak and off-peak hours (admins)

export interface RateSchedule {
  id: string;
  name: string;
  periods: TouPeriod[];
  siteCount: number;
  /** "Organisation · Site" for each site that uses it. */
  sites: string[];
  createdAt: string;
  updatedAt: string;
}

export const rateSchedulesKey = ['rate-schedules'] as const;
export const useRateSchedules = (enabled = true) => useQuery({ queryKey: rateSchedulesKey, queryFn: () => get<RateSchedule[]>('/rate-schedules'), enabled });
function useScheduleMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rateSchedulesKey });
      void qc.invalidateQueries({ queryKey: chargersKey });
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}
export const useSaveRateSchedule = () =>
  useScheduleMutation(({ id, ...body }: RateScheduleBody & { id?: string }) => (id ? put<RateSchedule>(`/rate-schedules/${id}`, body) : post<RateSchedule>('/rate-schedules', body)));
export const useDeleteRateSchedule = () => useScheduleMutation((id: string) => del(`/rate-schedules/${id}`));
